import { useMemo, useRef, useState } from "react";
import {
  applyPaymentImportRequest,
  isApiRequestError,
  previewPaymentImportRequest,
  type PaymentImportApplyItem,
  type PaymentImportPreviewResponse,
  type PaymentImportPreviewRow,
} from "../../../../lib/api";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onApplied: (summary: { importedRows: number; importedAmount: number }) => void;
}

function fmtMoney(value: number) {
  return value.toLocaleString("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 2,
  });
}

function confidenceBadge(confidence: PaymentImportPreviewRow["confidence"]) {
  if (confidence === "high") {
    return "bg-green-100 text-green-700 border-green-200";
  }
  if (confidence === "medium") {
    return "bg-amber-100 text-amber-700 border-amber-200";
  }
  if (confidence === "low") {
    return "bg-sky-100 text-sky-700 border-sky-200";
  }
  return "bg-rose-100 text-rose-700 border-rose-200";
}

function confidenceText(confidence: PaymentImportPreviewRow["confidence"]) {
  if (confidence === "high") return "Yüksek";
  if (confidence === "medium") return "Orta";
  if (confidence === "low") return "Düşük";
  return "Yok";
}

function buildApplyItems(rows: PaymentImportPreviewRow[], selectedRows: number[]): PaymentImportApplyItem[] {
  return rows
    .filter((row) => selectedRows.includes(row.row_number) && row.can_apply)
    .map((row) => ({
      row_number: row.row_number,
      matched_customer_id: row.matched_customer_id ?? null,
      matched_share_ids: row.matched_share_ids,
      payment_date: row.payment_date,
      amount: row.amount,
      payment_type: row.payment_type,
      description: row.description || null,
      fis_no: row.fis_no || null,
      reference_no: row.reference_no || null,
      sender_name: row.sender_name || null,
    }));
}

const SAMPLE_TEMPLATE = [
  ["Tarih", "Gönderen Adı", "Telefon", "Tutar", "Açıklama", "Referans No", "Fiş No", "Küpe No", "Sipariş No", "Hisse No", "Ödeme Tipi"],
  ["2026-06-06", "Ahmet Yılmaz", "05551234567", "12500", "OPS-2026-0004 kapora", "TRX-98431", "OPS-2026-0004", "TR-145", "14", "1/7", "Havale"],
].map((row) => row.join(";")).join("\n");

export default function BankImportModal({ isOpen, onClose, onApplied }: Props) {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<PaymentImportPreviewResponse | null>(null);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState("");

  const selectedApplyItems = useMemo(
    () => (preview ? buildApplyItems(preview.rows, selectedRows) : []),
    [preview, selectedRows]
  );

  if (!isOpen) return null;

  async function handlePreview() {
    if (!selectedFile) {
      setError("Önce .xlsx veya .csv dosyasını seç.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const result = await previewPaymentImportRequest(selectedFile);
      setPreview(result);
      setSelectedRows(result.rows.filter((row) => row.can_apply).map((row) => row.row_number));
    } catch (err) {
      setPreview(null);
      setSelectedRows([]);
      setError(isApiRequestError(err) ? err.detail ?? err.message : "Dosya okunamadı.");
    } finally {
      setLoading(false);
    }
  }

  async function handleApply() {
    if (!preview) return;

    if (selectedApplyItems.length === 0) {
      setError("İşlenecek eşleşmiş satır seçilmedi.");
      return;
    }

    setApplying(true);
    setError("");

    try {
      const result = await applyPaymentImportRequest({ items: selectedApplyItems });
      onApplied({
        importedRows: result.imported_rows,
        importedAmount: result.imported_amount,
      });
      setSelectedFile(null);
      setPreview(null);
      setSelectedRows([]);
      onClose();
    } catch (err) {
      setError(isApiRequestError(err) ? err.detail ?? err.message : "İçeri alma yapılamadı.");
    } finally {
      setApplying(false);
    }
  }

  function toggleRow(rowNumber: number) {
    setSelectedRows((prev) =>
      prev.includes(rowNumber) ? prev.filter((item) => item !== rowNumber) : [...prev, rowNumber]
    );
  }

  function selectOnlyReady() {
    if (!preview) return;
    setSelectedRows(preview.rows.filter((row) => row.can_apply).map((row) => row.row_number));
  }

  function clearSelection() {
    setSelectedRows([]);
  }

  return (
    <div className="fixed inset-0 z-[120] bg-black/50 flex items-center justify-center p-4">
      <div className="w-full max-w-6xl max-h-[92vh] overflow-hidden rounded-3xl border border-farm-border bg-white shadow-2xl flex flex-col">
        <div className="px-5 py-4 border-b border-farm-border flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold text-farm-brown">Excel ile Havale Yükle</h2>
            <p className="text-sm text-farm-muted mt-1">
              .xlsx veya .csv dosyasını yükle, sistem eşleştirmeyi önersin, onayladıklarını tek seferde ödemeye dönüştürelim.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="w-10 h-10 rounded-xl border border-farm-border text-farm-muted hover:text-farm-brown hover:border-farm-earth"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="px-5 py-4 border-b border-farm-border bg-farm-cream/60">
          <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-4">
            <div className="rounded-2xl border border-farm-border bg-white p-4">
              <p className="text-sm font-semibold text-farm-brown">Dosya seç</p>
              <p className="text-xs text-farm-muted mt-1">
                En doğru eşleşme için açıklama alanında fiş no veya küpe no bulunması çok faydalıdır.
              </p>

              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="h-10 px-4 rounded-xl border border-farm-border bg-farm-cream text-farm-brown text-sm font-semibold"
                >
                  Dosya Seç
                </button>
                <button
                  type="button"
                  onClick={handlePreview}
                  disabled={loading}
                  className="h-10 px-4 rounded-xl bg-farm-earth text-white text-sm font-semibold disabled:opacity-60"
                >
                  {loading ? "Önizleme Hazırlanıyor..." : "Önizleme Oluştur"}
                </button>
                <a
                  href={`data:text/csv;charset=utf-8,${encodeURIComponent(SAMPLE_TEMPLATE)}`}
                  download="evrenciftligi-havale-sablon.csv"
                  className="h-10 px-4 rounded-xl border border-farm-border bg-white text-farm-brown text-sm font-semibold inline-flex items-center"
                >
                  Örnek Şablon İndir
                </a>
              </div>

              <input
                ref={fileRef}
                type="file"
                accept=".xlsx,.csv,.txt"
                className="hidden"
                onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
              />

              <div className="mt-3 rounded-xl border border-dashed border-farm-border bg-farm-cream/70 px-3 py-2 text-sm text-farm-brown">
                {selectedFile ? `${selectedFile.name} seçildi` : "Henüz dosya seçilmedi"}
              </div>
            </div>

            <div className="rounded-2xl border border-farm-border bg-white p-4">
              <p className="text-sm font-semibold text-farm-brown">Minimum kolon önerisi</p>
              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                {[
                  "tarih",
                  "gonderen_adi",
                  "telefon",
                  "tutar",
                  "aciklama",
                  "referans_no",
                  "fis_no",
                  "kupe_no",
                  "hisse_no",
                ].map((item) => (
                  <span key={item} className="px-2.5 py-1 rounded-full bg-farm-cream border border-farm-border text-farm-brown">
                    {item}
                  </span>
                ))}
              </div>

              <div className="mt-4 space-y-2 text-xs text-farm-muted">
                <p>1. Öncelik: Fiş No</p>
                <p>2. Öncelik: Küpe No + Hisse No</p>
                <p>3. Öncelik: Telefon</p>
                <p>4. Öncelik: Gönderen Adı</p>
              </div>
            </div>
          </div>

          {error && (
            <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {error}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-auto p-5 bg-farm-cream/40">
          {!preview ? (
            <div className="rounded-2xl border border-dashed border-farm-border bg-white p-6 text-center text-sm text-farm-muted">
              Dosyayı seçip önizleme oluşturduğunda eşleşme önerileri burada görünecek.
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
                <SummaryCard label="Toplam Satır" value={String(preview.summary.total_rows)} />
                <SummaryCard label="Hazır" value={String(preview.summary.ready_to_apply)} />
                <SummaryCard label="Yüksek" value={String(preview.summary.high_confidence)} />
                <SummaryCard label="Orta" value={String(preview.summary.medium_confidence)} />
                <SummaryCard label="Düşük" value={String(preview.summary.low_confidence)} />
                <SummaryCard label="Eşleşmeyen" value={String(preview.summary.unmatched)} />
              </div>

              <div className="rounded-2xl border border-farm-border bg-white overflow-hidden">
                <div className="px-4 py-3 border-b border-farm-border flex flex-wrap items-center gap-2 justify-between">
                  <div>
                    <p className="text-sm font-semibold text-farm-brown">Önizleme</p>
                    <p className="text-xs text-farm-muted">Seçili satırlar içeri alınacak.</p>
                  </div>

                  <div className="flex gap-2">
                    <button type="button" onClick={selectOnlyReady} className="h-9 px-3 rounded-xl border border-farm-border text-sm text-farm-brown bg-farm-cream">
                      Hazır Olanları Seç
                    </button>
                    <button type="button" onClick={clearSelection} className="h-9 px-3 rounded-xl border border-farm-border text-sm text-farm-muted bg-white">
                      Seçimi Temizle
                    </button>
                  </div>
                </div>

                <div className="overflow-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-farm-cream/80 text-farm-brown">
                      <tr>
                        <th className="px-3 py-3 text-left">Seç</th>
                        <th className="px-3 py-3 text-left">Satır</th>
                        <th className="px-3 py-3 text-left">Durum</th>
                        <th className="px-3 py-3 text-left">Gönderen</th>
                        <th className="px-3 py-3 text-left">Tutar</th>
                        <th className="px-3 py-3 text-left">Önerilen Müşteri</th>
                        <th className="px-3 py-3 text-left">Eşleşme</th>
                        <th className="px-3 py-3 text-left">Notlar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.rows.map((row) => {
                        const checked = selectedRows.includes(row.row_number);
                        return (
                          <tr key={row.row_number} className="border-t border-farm-border align-top">
                            <td className="px-3 py-3">
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={!row.can_apply}
                                onChange={() => toggleRow(row.row_number)}
                                className="mt-1"
                              />
                            </td>
                            <td className="px-3 py-3 font-mono text-xs text-farm-muted">{row.row_number}</td>
                            <td className="px-3 py-3">
                              <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${confidenceBadge(row.confidence)}`}>
                                {confidenceText(row.confidence)}
                              </span>
                            </td>
                            <td className="px-3 py-3 min-w-[180px]">
                              <div className="font-medium text-farm-brown">{row.sender_name || "-"}</div>
                              <div className="text-xs text-farm-muted">{row.phone || "Telefon yok"}</div>
                              <div className="text-xs text-farm-muted mt-1">{row.payment_date || "Tarih yok"}</div>
                            </td>
                            <td className="px-3 py-3 whitespace-nowrap font-semibold text-farm-brown">{fmtMoney(row.amount)}</td>
                            <td className="px-3 py-3 min-w-[220px]">
                              <div className="font-medium text-farm-brown">{row.matched_customer_name || "Eşleşmedi"}</div>
                              {row.matched_phone && <div className="text-xs text-farm-muted">{row.matched_phone}</div>}
                              {row.matched_open_total > 0 && (
                                <div className="text-xs text-farm-muted mt-1">Açık bakiye: {fmtMoney(row.matched_open_total)}</div>
                              )}
                            </td>
                            <td className="px-3 py-3 min-w-[220px]">
                              <div className="text-farm-brown">{row.match_reason || "-"}</div>
                              <div className="text-xs text-farm-muted mt-1 flex flex-wrap gap-1">
                                {row.fis_no && <span>Fiş: {row.fis_no}</span>}
                                {row.kupe_no && <span>Küpe: {row.kupe_no}</span>}
                                {row.hisse_no && <span>Hisse: {row.hisse_no}</span>}
                              </div>
                            </td>
                            <td className="px-3 py-3 min-w-[260px]">
                              {row.issues.length > 0 ? (
                                <ul className="space-y-1 text-xs text-rose-700">
                                  {row.issues.map((issue) => (
                                    <li key={issue}>• {issue}</li>
                                  ))}
                                </ul>
                              ) : (
                                <div className="text-xs text-green-700">İşlemeye hazır</div>
                              )}
                              {row.description && (
                                <div className="mt-2 text-xs text-farm-muted">Açıklama: {row.description}</div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-farm-border bg-white flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-farm-muted">
            {selectedApplyItems.length > 0 ? `${selectedApplyItems.length} satır işlenecek` : "Henüz işlenecek satır seçilmedi"}
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="h-11 px-4 rounded-xl border border-farm-border bg-white text-farm-muted"
            >
              Kapat
            </button>
            <button
              type="button"
              onClick={handleApply}
              disabled={applying || selectedApplyItems.length === 0}
              className="h-11 px-4 rounded-xl bg-farm-earth text-white font-semibold disabled:opacity-60"
            >
              {applying ? "İçeri Alınıyor..." : "Seçili Satırları İçeri Al"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-farm-border bg-white px-4 py-3">
      <div className="text-xs font-semibold uppercase tracking-wide text-farm-muted">{label}</div>
      <div className="mt-1 text-lg font-bold text-farm-brown">{value}</div>
    </div>
  );
}
